/**
 * LifecycleArtifactPickerDialog — modal para añadir un entregable
 * (sub-fase + artifact) a una phase del lifecycle. (B.6.d.9.i)
 *
 * Cada entregable es 1:1 sub-fase ↔ expected_artifact. El modal pide:
 *   1. Plantilla del artefacto (catálogo de tenant, tipo `artifact`).
 *   2. Nombre humano de la sub-fase (auto-suggest "Crear <Template>",
 *      editable).
 *   3. Configuración: cantidad inicial, opcional, permitir añadir más
 *      instancias en runtime (`user_can_add_more`).
 *
 * Al confirmar dispatcha `ADD_DELIVERABLE` que crea de un solo golpe
 * la entrada en `phase.subphases[]` y en `phase.expected_artifacts[]`.
 *
 * El `subphase_code` se auto-genera en el reducer; el usuario no lo ve.
 *
 * OT-0055 — plantillas y roles ofrecidos: los de la metodología del ciclo
 * y los genéricos (`citableTemplates`), lo que la completitud acepta.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileText, Search, X } from 'lucide-react'

import { listTemplates, type Template } from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import { citableTemplates, sortedCitable } from '../../../lib/methodologyScope'
import type {
  LifecycleDesignerState,
  LifecycleDesignerAction,
} from './lifecycleState'
import { DEFAULT_LOCALE } from '../../../i18n/config'

interface LifecycleArtifactPickerDialogProps {
  open: boolean
  state: LifecycleDesignerState
  dispatch: React.Dispatch<LifecycleDesignerAction>
  /** Phase donde se está añadiendo el entregable. */
  phaseCode: string | null
  /** Metodología del ciclo: filtra plantillas y roles ofrecidos. */
  methodologyId: string | null
  onClose: () => void
}

export function LifecycleArtifactPickerDialog(props: LifecycleArtifactPickerDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca con el estado
  // inicial, sin reiniciarlo con efectos.
  if (!props.open) return null
  return <PickerContent {...props} />
}

function PickerContent({
  state,
  dispatch,
  phaseCode,
  methodologyId,
  onClose,
}: LifecycleArtifactPickerDialogProps) {
  const { t, i18n } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const [query, setQuery] = useState('')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  // `null` mientras el usuario no lo edite: se sugiere desde la plantilla.
  const [typedName, setTypedName] = useState<string | null>(null)
  const [defaultCount, setDefaultCount] = useState<number>(1)
  const [isOptional, setIsOptional] = useState(false)
  const [userCanAddMore, setUserCanAddMore] = useState(false)
  const [roleTemplateKey, setRoleTemplateKey] = useState<string>('')

  // Bloqueo scroll + Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  // Catálogo de artifacts.
  const { data: catalog = [], isLoading } = useQuery({
    queryKey: ['templates', 'list', 'artifact', query],
    queryFn: () =>
      listTemplates(accessToken!, {
        template_type: 'artifact',
        search: query.trim() || undefined,
      }),
    enabled: Boolean(accessToken),
    staleTime: 30_000,
  })
  const templates = useMemo(
    () => [...citableTemplates(catalog, methodologyId).values()],
    [catalog, methodologyId],
  )

  // Catálogo de roles para el dropdown "Rol responsable" — solo cargamos
  // una vez al abrir el modal (no se filtra por search).
  const { data: roles = [] } = useQuery({
    queryKey: ['templates', 'list', 'role'],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'role' }),
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
  const sortedRoles = useMemo(
    () => sortedCitable(roles, methodologyId, i18n.language || DEFAULT_LOCALE),
    [roles, methodologyId, i18n.language],
  )

  // Nombre de la sub-fase: el que escribió el usuario o, mientras no lo
  // edite, el sugerido desde la plantilla seleccionada.
  const selectedTemplate: Template | undefined = useMemo(
    () => templates.find((tpl) => tpl.template_key === selectedKey),
    [templates, selectedKey],
  )
  const subphaseName =
    typedName ??
    (selectedTemplate
      ? t('designer.lifecycle.deliverable_name_autofill', { template: selectedTemplate.name })
      : '')

  // OT-0007 §1 — quién produce el documento (plantilla vs. archivo) ya
  // no se decide al diseñar el lifecycle: es una elección por artifact
  // en Lifecycle, en runtime (`ArtifactSourceModeSelect`). Este picker
  // siempre pide plantilla — 4 secciones fijas, sin ramas por modo.
  const phase = phaseCode
    ? state.definition.phases.find((p) => p.phase_code === phaseCode)
    : undefined
  const usedTemplateKeys = useMemo(() => {
    const set = new Set<string>()
    for (const a of phase?.expected_artifacts ?? []) {
      if (a.template_key) set.add(a.template_key)
    }
    return set
  }, [phase])

  function commit() {
    if (!phaseCode) return
    if (!subphaseName.trim()) return
    // Siempre requerimos selección de plantilla (OT-0007 — ver comentario
    // arriba de `phase`).
    if (!selectedKey) return
    if (usedTemplateKeys.has(selectedKey)) return
    dispatch({
      type: 'ADD_DELIVERABLE',
      phaseCode,
      templateKey: selectedKey,
      subphaseName: subphaseName.trim(),
      defaultCount: Math.max(1, Math.floor(defaultCount)),
      isOptional,
      userCanAddMore,
      roleTemplateKey: roleTemplateKey.trim() || null,
    })
    onClose()
  }

  const canSubmit =
    Boolean(selectedKey) &&
    Boolean(subphaseName.trim()) &&
    !usedTemplateKeys.has(selectedKey ?? '')

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 backdrop-blur-sm overflow-y-auto py-12"
      role="dialog"
      aria-modal="true"
      aria-label={t('designer.lifecycle.deliverable_picker_title')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="mx-4 w-full max-w-2xl rounded-md border border-line bg-surface shadow-lg flex flex-col max-h-[calc(100vh-6rem)]">
        {/* Header */}
        <header className="px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                {t('designer.lifecycle.deliverable_picker_eyebrow', {
                  phase: phaseCode,
                })}
              </p>
              <h2 className="mt-1 font-display text-xl font-semibold text-ink">
                {t('designer.lifecycle.deliverable_picker_title')}
              </h2>
              <p className="mt-1 text-xs text-ink-muted">
                {t('designer.lifecycle.deliverable_picker_subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('designer.picker.close')}
              className="p-1 rounded text-ink-subtle hover:text-ink hover:bg-surface-muted shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Body — scrolleable */}
        <div className="flex-1 overflow-y-auto">
          {/* ─── Sección 1: plantilla (solo internal) ─────────────── */}
          <Section
            number={1}
            title={t('designer.lifecycle.deliverable_section_template')}
            hint={t('designer.lifecycle.deliverable_section_template_hint')}
          >
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t(
                  'designer.lifecycle.artifact_picker_search_placeholder',
                )}
                className="w-full pl-9 pr-3 py-2 bg-canvas border border-line rounded-md text-sm focus:outline-none focus:border-brand"
              />
            </div>
            {isLoading ? (
              <p className="text-center font-body text-sm text-ink-subtle py-6">
                {t('catalog.loading')}
              </p>
            ) : templates.length === 0 ? (
              <p className="text-center font-body text-sm text-ink-subtle py-6">
                {query.trim()
                  ? t('designer.lifecycle.artifact_picker_empty_search', {
                      query,
                    })
                  : t('designer.lifecycle.artifact_picker_empty_catalog')}
              </p>
            ) : (
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {templates.map((tpl) => {
                  const selected = selectedKey === tpl.template_key
                  const disabled = usedTemplateKeys.has(tpl.template_key)
                  return (
                    <li key={tpl.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (!disabled) setSelectedKey(tpl.template_key)
                        }}
                        disabled={disabled}
                        className={
                          disabled
                            ? 'w-full text-left rounded-md border border-line bg-surface-muted/30 p-3 opacity-60 cursor-not-allowed flex items-start gap-3'
                            : selected
                              ? 'w-full text-left rounded-md border border-brand bg-brand-soft/40 p-3 flex items-start gap-3'
                              : 'w-full text-left rounded-md border border-line bg-canvas hover:border-line-strong hover:bg-surface-muted/40 p-3 flex items-start gap-3 cursor-pointer'
                        }
                      >
                        <FileText className="w-4 h-4 mt-0.5 shrink-0 text-ink-muted" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-display text-sm font-semibold text-ink">
                              {tpl.name}
                            </span>
                            {tpl.is_system_default ? (
                              <span className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-official-soft text-official">
                                system
                              </span>
                            ) : null}
                            {disabled ? (
                              <span className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-surface-muted text-ink-subtle">
                                {t(
                                  'designer.lifecycle.deliverable_already_added',
                                )}
                              </span>
                            ) : null}
                          </span>
                          {tpl.description ? (
                            <span className="block mt-0.5 text-xs text-ink-muted line-clamp-2">
                              {tpl.description}
                            </span>
                          ) : null}
                          <span className="block mt-1 font-mono text-[10px] text-ink-subtle">
                            {tpl.template_key}
                          </span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </Section>

          {/* ─── Sección 2: nombre de la sub-fase ─────────────────── */}
          <Section
            number={2}
            title={t('designer.lifecycle.deliverable_section_subphase')}
            hint={t('designer.lifecycle.deliverable_section_subphase_hint')}
            disabled={!selectedKey}
          >
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                {t('designer.lifecycle.deliverable_subphase_name_label')}
              </span>
              <input
                type="text"
                value={subphaseName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={!selectedKey}
                placeholder={t(
                  'designer.lifecycle.deliverable_subphase_name_placeholder',
                )}
                className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
              />
              <span className="mt-1 block text-[11px] text-ink-subtle">
                {t('designer.lifecycle.deliverable_subphase_name_help')}
              </span>
            </label>
          </Section>

          {/* ─── Sección 3: configuración ─────────────────────────── */}
          <Section
            number={3}
            title={t('designer.lifecycle.deliverable_section_config')}
            hint={t('designer.lifecycle.deliverable_section_config_hint')}
            disabled={!selectedKey}
          >
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                  {t('designer.lifecycle.artifact_default_count_label')}
                </span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={defaultCount}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    setDefaultCount(Number.isFinite(n) && n >= 1 ? n : 1)
                  }}
                  disabled={!selectedKey}
                  className="mt-1 w-32 bg-canvas border border-line rounded-md px-3 py-1.5 text-sm font-mono disabled:opacity-60"
                />
                <span className="mt-1 block text-[11px] text-ink-subtle">
                  {t('designer.lifecycle.artifact_default_count_help')}
                </span>
              </label>

              <ToggleField
                label={t('designer.lifecycle.artifact_is_optional_label')}
                help={t('designer.lifecycle.artifact_is_optional_help')}
                checked={isOptional}
                disabled={!selectedKey}
                onChange={setIsOptional}
              />

              <ToggleField
                label={t('designer.lifecycle.artifact_user_can_add_more_label')}
                help={t('designer.lifecycle.artifact_user_can_add_more_help')}
                checked={userCanAddMore}
                disabled={!selectedKey}
                onChange={setUserCanAddMore}
              />
            </div>
          </Section>

          {/* ─── Sección 4: rol responsable ──────────────────────── */}
          <Section
            number={4}
            title={t('designer.lifecycle.deliverable_section_role')}
            hint={t('designer.lifecycle.deliverable_section_role_hint')}
            disabled={!selectedKey}
          >
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                {t('designer.lifecycle.deliverable_role_label')}
              </span>
              <select
                value={roleTemplateKey}
                onChange={(e) => setRoleTemplateKey(e.target.value)}
                disabled={!selectedKey}
                className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
              >
                <option value="">
                  — {t('designer.lifecycle.deliverable_role_unassigned')} —
                </option>
                {sortedRoles.map((role) => (
                  <option key={role.id} value={role.template_key}>
                    {role.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] text-ink-subtle">
                {t('designer.lifecycle.deliverable_role_help')}
              </span>
            </label>
          </Section>
        </div>

        {/* Footer */}
        <footer className="border-t border-line px-6 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md"
          >
            {t('designer.picker.cancel')}
          </button>
          <button
            type="button"
            onClick={commit}
            disabled={!canSubmit}
            className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('designer.lifecycle.deliverable_picker_add')}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────
function Section({
  number,
  title,
  hint,
  disabled,
  children,
}: {
  number: number
  title: string
  hint?: string
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <section
      className={`px-6 py-4 border-b border-line last:border-b-0 ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <header className="mb-2.5 flex items-baseline gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-soft text-brand text-[10px] font-semibold tabular-nums">
          {number}
        </span>
        <h3 className="font-display text-sm font-semibold text-ink">{title}</h3>
      </header>
      {hint ? (
        <p className="text-[11px] text-ink-subtle italic mb-3">{hint}</p>
      ) : null}
      {children}
    </section>
  )
}

function ToggleField({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string
  help?: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 accent-brand"
      />
      <div>
        <span className="text-sm text-ink">{label}</span>
        {help ? (
          <span className="block text-[11px] text-ink-subtle">{help}</span>
        ) : null}
      </div>
    </label>
  )
}
